import {
  Button,
  Col,
  Drawer,
  Grid,
  Image,
  notification,
  Radio,
  RadioChangeEvent,
  Row,
  Table,
  TableProps,
} from "antd";
import AcceptanceConditionModal from "components/Desktop/AcceptanceConditionModal";
import ReceiptModal from "components/Desktop/ReceiptModal";
import NarinBreadcrumb from "components/Desktop/Breadcrumb";
import { PriceRow } from "components/PriceRow";
import NarinLayout from "components/NarinLayout";
import { IMAGE_NOT_FOUND } from "constants/common";
import { E_DELIVERY_METHOD } from "constants/delivery";
import { E_PAYMENT_METHOD } from "constants/payment";
import ArrowDownIcon from "icons/ArrowDownIcon";
import ArrowUpIcon from "icons/ArrowUpIcon";
import StarIcon from "icons/StarIcon";
import { ChevronRight, Ticket } from "lucide-react";
import AddressCard from "pages/Checkout/components/AddressCard";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetUserAddress } from "services/address";
import {
  useAcceptCondition,
  useCheckAcceptanceCondition,
} from "services/acceptanceCondition";
import { useCheckoutCart } from "services/order";
import { useApplyPromotionCode } from "services/promotionCode";
import useCartStore from "stores/cart";
import PromotionCodeDrawer from "./components/PromotionCodeDrawer";
import PointsToggle from "./components/PointsToggle";
import MobileCartItem from "components/Mobile/MobileCartItem";
import { getImageFullPath } from "utils/image";
import { RedemptionItem } from "types/redemption";

interface DataType {
  id: number;
  key: number;
  productName: string;
  price: string;
  qty: number;
  imageUrl: string;
  productId: number;
  isRedemptionItem?: boolean;
  redemptionItem?: RedemptionItem; // RedemptionItem type
}

const CheckoutPage = () => {
  const { checkoutCartAsync, isPending } = useCheckoutCart();
  const { checkAcceptanceConditionAsync } = useCheckAcceptanceCondition();
  const { acceptConditionAsync } = useAcceptCondition();
  const { userAddress } = useGetUserAddress();
  const navigate = useNavigate();
  const { productItemsSelected, setProductItemsSelected } = useCartStore();
  const { applyPromotionCodeAsync, discountAmount } = useApplyPromotionCode();

  const [userAddressId, setUserAddressId] = useState<number>();
  const [openCartDrawer, setOpenCartDrawer] = useState<boolean>(false);
  const [openShippingDrawer, setOpenShippingDrawer] = useState<boolean>(false);
  const [receiptModalVisible, setReceiptModalVisible] =
    useState<boolean>(false);
  const [acceptanceModalVisible, setAcceptanceModalVisible] =
    useState<boolean>(false);
  const [isFirstTimeAcceptance, setIsFirstTimeAcceptance] =
    useState<boolean>(true);
  const [orderId, setOrderId] = useState<number | null>(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const onOpenDrawer = () => {
    setOpenCartDrawer(true);
  };

  const onCloseDrawer = () => {
    setOpenCartDrawer(false);
  };

  const [promotionCode, setPromotionCode] = useState<string>("");
  const [shippingType, setShippingType] = useState<string>(
    E_DELIVERY_METHOD.TRANSFER
  );
  const [paymentMethod, setPaymentMethod] = useState<string>(
    E_PAYMENT_METHOD.TRANSFER
  );
  const [showShippingDetail, setShowShippingDetail] = useState<boolean>(false);
  const [coinsDiscount, setCoinsDiscount] = useState<number>(0);
  const [pointsUsed, setPointsUsed] = useState<number>(0);

  useEffect(() => {
    if (userAddress && userAddress.length !== 0) {
      const defaultAddress = userAddress.find((address) => address.isDefault);
      if (defaultAddress) {
        setUserAddressId(defaultAddress.id);
      }
    }
  }, [userAddress]);
  useEffect(() => {
    if (productItemsSelected.length === 0) {
      navigate("/");
    }
  }, [navigate, productItemsSelected.length]);

  // if (productItemsSelected.length === 0) {
  //   return navigate("/");
  // }

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: "12%",
      render: (text: string) => {
        return (
          <Image
            src={`${getImageFullPath(text)}`}
            width={90}
            height={90}
            alt={text}
            className="object-contain"
            fallback={IMAGE_NOT_FOUND}
          />
        );
      },
    },
    {
      title: "สินค้า",
      dataIndex: "productName",
      key: "productName",
      width: "43%",
      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "ราคาต่อหน่วย",
      dataIndex: "price",
      key: "price",
      width: "15%",
      render: (text: number, record: DataType) => {
        if (record.isRedemptionItem) {
          return (
            <div>
              <div className="text-primary-500">
                {record.redemptionItem?.pointsCost} คะแนน
              </div>
              {record.redemptionItem?.type === "HYBRID" && (
                <div className="text-sm">+ {text.toLocaleString()} บาท</div>
              )}
            </div>
          );
        }
        return <div>{text.toLocaleString()} บาท</div>;
      },
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      key: "qty",
      width: "15%",
    },
    {
      title: "ราคารวม",
      dataIndex: "sum",
      key: "sum",
      width: "15%",
      render: (_: string, record: DataType) => {
        if (record.isRedemptionItem && record.redemptionItem) {
          return (
            <div>
              <div className="text-primary-500">
                {record.redemptionItem?.pointsCost * record.qty} คะแนน
              </div>
              {record.redemptionItem?.type === "HYBRID" && (
                <div className="text-sm">
                  + {(record.qty * parseFloat(record.price)).toLocaleString()}{" "}
                  บาท
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            {(record.qty * parseFloat(record.price)).toLocaleString()} บาท
          </div>
        );
      },
    },
  ];

  // Calculate totals for selected items (cash and points separately)
  const totals = productItemsSelected.reduce(
    (acc, cur) => {
      if (cur.isRedemptionItem && cur.redemptionItem) {
        // For redemption items, add points and cash separately
        acc.totalPoints += cur.redemptionItem.pointsCost * cur.qty;
        if (cur.redemptionItem.type === "HYBRID") {
          acc.totalCash += parseFloat(cur.price) * cur.qty;
        }
      } else {
        // For regular products, add to cash total
        acc.totalCash += parseFloat(cur.price) * cur.qty;
      }
      return acc;
    },
    { totalCash: 0, totalPoints: 0 }
  );

  // Legacy sum for backward compatibility (cash only)
  const sum = totals.totalCash;

  // Extract product IDs from selected items
  const productIds = productItemsSelected.map(
    (item) => item.productId || parseInt(item.id.toString())
  );

  const onCheckout = async () => {
    try {
      // Check if cart contains redemption items and validate them using cart-level validation
      // if (hasRedemptionItems(cartItems)) {
      //   const validationResult = await validateCartRedemptionsAtCheckout(
      //     cartItems
      //   );

      //   if (!validationResult.isValid) {
      //     notification.error({
      //       message: "การตรวจสอบรายการแลกซื้อล้มเหลว",
      //       description: formatRedemptionValidationErrors(
      //         validationResult.errors
      //       ),
      //       duration: 5,
      //     });
      //     return; // Stop checkout process
      //   }
      // }

      const result = await checkoutCartAsync({
        cartItemIds: productItemsSelected.map((selected) => selected.id),
        paymentMethod: paymentMethod,
        userAddressId: userAddressId || userAddress[0]?.id,
        deliveryMethod: shippingType,
        promotionCode,
        pointsAsCashToUse: pointsUsed > 0 ? pointsUsed : undefined,
      });

      // Store order ID for later use
      const orderId = result.data.id;

      // Check acceptance conditions
      const { data: acceptanceData } = await checkAcceptanceConditionAsync();
      if (acceptanceData && acceptanceData.needsAcceptance.needsAcceptance) {
        setIsFirstTimeAcceptance(acceptanceData.needsAcceptance.isFirst);
        // Store order ID in a state variable for use in the modal
        setOrderId(orderId);
        setAcceptanceModalVisible(true);
      } else {
        // If both conditions are already accepted, navigate to order detail
        navigate(`/orderdetail/${orderId}`);
      }
    } catch (error: unknown) {
      const e = error as { response?: { data?: { message?: string } } };
      if (e.response?.data?.message === "USER_ADDRESS_NOT_FOUND") {
        notification.error({
          message: "เกิดข้อผิดพลาด",
          description:
            "กรุณาเพิ่มที่อยู่จัดส่งก่อนยืนยันการสั่งซื้อ เนื่องจากวิธีการจัดส่งที่เลือกจำเป็นต้องใช้ข้อมูลที่อยู่",
        });
        return;
      }

      notification.error({
        message: "เกิดข้อผิดพลาด",
        description:
          e.response?.data?.message ||
          "ไม่สามารถทำรายการได้ กรุณาลองใหม่อีกครั้ง",
      });
    }
  };

  const onChangeUserAddress = (value: number) => {
    setUserAddressId(value);
  };

  const onChangePaymentMethod = (e: RadioChangeEvent) => {
    setPaymentMethod(e.target.value);
  };

  const onChangeShippingType = (e: RadioChangeEvent) => {
    setShippingType(e.target.value);
    setOpenShippingDrawer(false);
  };

  const applyPromotionCode = async (selected: boolean, code: string) => {
    if (selected) {
      setPromotionCode("");
      // Reset points toggle when removing promotion code
      setCoinsDiscount(0);
      setPointsUsed(0);
    } else {
      try {
        setPromotionCode(code);
        // Reset points toggle when applying new promotion code
        setCoinsDiscount(0);
        setPointsUsed(0);
        await applyPromotionCodeAsync({
          code: code,
          orderAmount: sum,
          productIds: productIds, // Include product IDs for validation
        });
        notification.success({
          message: "ใช้โค้ดส่วนลดสำเร็จ",
          description: `ใช้โค้ด ${code} สำเร็จแล้ว`,
          duration: 2,
        });
      } catch (error: unknown) {
        const e = error as { response?: { data?: { message?: string } } };
        notification.error({
          message: "ไม่สามารถใช้โค้ดส่วนลดได้",
          description: e.response?.data?.message || "เกิดข้อผิดพลาด",
          duration: 2,
        });
        setPromotionCode("");
        // Reset points toggle when promotion code fails
        setCoinsDiscount(0);
        setPointsUsed(0);
      }
    }
  };

  const getShippingMethodLabel = (method: string) => {
    switch (method) {
      case E_DELIVERY_METHOD.TRANSFER:
        return "จัดส่งสินค้าผ่านขนส่งเอกชน";
      case E_DELIVERY_METHOD.NARIN_TRANSFER:
        return "รับสินค้าผ่านรถขนส่งของทางร้าน";
      case E_DELIVERY_METHOD.AROUND_CITY:
        return "รับสินค้าภายในเขตเทศบาลเมืองชัยภูมิ";
      case E_DELIVERY_METHOD.RESELL_BRANCH:
        return "รับสินค้าที่บริษัทนรินทร์เภสัช จำกัด สาขาเยื้องประปาชัยภูมิ";
      case E_DELIVERY_METHOD.MAIN_BRANCH:
        return "รับสินค้าที่ บริษัทนรินทร์เภสัช จำกัด สาขา สาขาเทศบาล 1";
      default:
        return "";
    }
  };

  return (
    <NarinLayout>
      <NarinBreadcrumb
        items={[
          {
            title: "สินค้าทั้งหมด",
            path: "/",
          },
          {
            title: "ตะกร้าสินค้า",
          },
        ]}
      />
      <div className="w-full bg-white py-8 px-4">
        <div className="flex justify-between items-center pb-2">
          <div className="md:text-xl text-base">ตะกร้าสินค้า</div>
          <Button
            onClick={() => setReceiptModalVisible(true)}
            size="large"
            type="primary"
          >
            สรุปรายการสั่งซื้อ
          </Button>
        </div>
        {screens.md ? (
          <div>
            {/* Products Section */}
            {productItemsSelected.filter((item) => !item.isRedemptionItem)
              .length > 0 && (
              <div className="mb-8">
                <div className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="mr-2">🛍️</span>
                  สินค้า (
                  {
                    productItemsSelected.filter(
                      (item) => !item.isRedemptionItem
                    ).length
                  }
                  )
                </div>
                <Table
                  columns={columns}
                  dataSource={productItemsSelected.filter(
                    (item) => !item.isRedemptionItem
                  )}
                  pagination={false}
                />
              </div>
            )}

            {/* Redemption Items Section */}
            {productItemsSelected.filter((item) => item.isRedemptionItem)
              .length > 0 && (
              <div className="mb-8">
                {productItemsSelected.filter((item) => !item.isRedemptionItem)
                  .length > 0 && (
                  <div className="border-t border-gray-200 my-6"></div>
                )}
                <div className="text-lg font-semibold text-purple-600 mb-4 flex items-center">
                  <span className="mr-2">🎁</span>
                  สินค้าแลกคะแนน (
                  {
                    productItemsSelected.filter((item) => item.isRedemptionItem)
                      .length
                  }
                  )
                </div>
                <Table
                  columns={columns}
                  dataSource={productItemsSelected.filter(
                    (item) => item.isRedemptionItem
                  )}
                  pagination={false}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            {/* Products Section */}
            {productItemsSelected.filter((item) => !item.isRedemptionItem)
              .length > 0 && (
              <div className="mb-6">
                <div className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="mr-2">🛍️</span>
                  สินค้า (
                  {
                    productItemsSelected.filter(
                      (item) => !item.isRedemptionItem
                    ).length
                  }
                  )
                </div>
                {productItemsSelected
                  .filter((item) => !item.isRedemptionItem)
                  .map((item, index) => (
                    <MobileCartItem
                      key={item.id}
                      item={item}
                      index={index}
                      readOnly={true}
                    />
                  ))}
              </div>
            )}

            {/* Redemption Items Section */}
            {productItemsSelected.filter((item) => item.isRedemptionItem)
              .length > 0 && (
              <div className="mb-6">
                {productItemsSelected.filter((item) => !item.isRedemptionItem)
                  .length > 0 && (
                  <div className="border-t border-gray-200 my-4"></div>
                )}
                <div className="text-lg font-semibold text-purple-600 mb-3 flex items-center">
                  <span className="mr-2">🎁</span>
                  สินค้าแลกคะแนน (
                  {
                    productItemsSelected.filter((item) => item.isRedemptionItem)
                      .length
                  }
                  )
                </div>
                {productItemsSelected
                  .filter((item) => item.isRedemptionItem)
                  .map((item, index) => (
                    <MobileCartItem
                      key={item.id}
                      item={item}
                      index={index}
                      readOnly={true}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="block lg:grid grid-cols-3 gap-4 mt-8">
        <div className="col-span-2 bg-white p-4">
          <div className="text-xl font-semibold">ข้อมูลการจัดส่ง</div>
          <AddressCard data={userAddress} onChange={onChangeUserAddress} />
          <div>
            <div className="font-semibold">สถานที่นัดรับ/จัดส่งสินค้า:</div>
            {screens.lg ? (
              <Radio.Group
                onChange={onChangeShippingType}
                style={{ width: "100%" }}
                value={shippingType}
              >
                <div className="flex gap-4 flex-col mt-4">
                  <div>
                    <Radio value={E_DELIVERY_METHOD.TRANSFER}>
                      จัดส่งสินค้าผ่านขนส่งเอกชน : จัดส่งสินค้าผ่านขนส่งเอกชน
                    </Radio>
                  </div>
                  <div>
                    <Radio
                      value={E_DELIVERY_METHOD.NARIN_TRANSFER}
                      className="radio-label-align-top"
                    >
                      <div>
                        <div className="flex gap-4">
                          <div>รับสินค้าผ่านรถขนส่งของทางร้าน</div>
                          <div
                            className="text-[#828282]"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowShippingDetail(!showShippingDetail);
                            }}
                          >
                            {showShippingDetail ? (
                              <div>
                                แสดงน้อยลง <ArrowUpIcon />
                              </div>
                            ) : (
                              <div>
                                ดูรายละเอียด <ArrowDownIcon />
                              </div>
                            )}
                          </div>
                        </div>
                        {showShippingDetail && (
                          <div className="animate-fadeIn">
                            <div>
                              &#x2022; วันจันทร์ : ในเมืองและในเขตอำเภอเมือง
                            </div>
                            <div>
                              &#x2022; วันอังคาร: แก้งคร้อ-คอนสวรรค์-บ้านแท่น
                            </div>
                            <div>
                              &#x2022; วันพุธ: บ้านเขว้า-หนองบัวแดง-ภักดีชุมพล
                            </div>
                            <div>
                              &#x2022; วันพฤหัสฯ: ภูเขียว-เกษตรสมบูรณ์-คอนสาร
                            </div>
                            <div>
                              &#x2022; วันศุกร์: จัตุรัส-เนินสง่า-บำเหน็จณรงค์
                            </div>
                            <div>
                              &#x2022; วันเสาร์: หนองบัวระเหว-ซับใหญ่-เทพสถิต
                            </div>
                          </div>
                        )}
                      </div>
                    </Radio>
                  </div>
                  <div>
                    <Radio
                      value={E_DELIVERY_METHOD.AROUND_CITY}
                      className="radio-label-align-top"
                    >
                      <div>รับสินค้าภายในเขตเทศบาลเมืองชัยภูมิ :</div>
                      <div>
                        &#x2022; ส่งฟรี รัศมีจากสาขาเยื้องประปา 1 ไม่เกิน 5
                        กิโลเมตร
                      </div>
                    </Radio>
                  </div>
                  <div>
                    <Radio value={E_DELIVERY_METHOD.RESELL_BRANCH}>
                      <div>
                        รับสินค้าที่บริษัทนรินทร์เภสัช จำกัด
                        สาขาเยื้องประปาชัยภูมิ :
                      </div>
                      <div>
                        &#x2022; เวลาทำการ วันจันทร์ - วันเสาร์ เวลา 08.00 น. -
                        18.00 น. 291 ถ.ชัยภูมิ-สีคิ้ว ต.ในเมือง อ.เมือง
                        จ.ชัยภูมิ 36000
                      </div>
                    </Radio>
                  </div>
                  <div>
                    <Radio value={E_DELIVERY_METHOD.MAIN_BRANCH}>
                      <div>
                        รับสินค้าที่ บริษัทนรินทร์เภสัช จำกัด สาขา สาขาเทศบาล 1
                      </div>
                      <div>
                        &#x2022; เวลาทำการ วันจันทร์ - วันเสาร์ เวลา 08.00 น. -
                        18.00 น. 237/35 ถ.ยุติธรรม ต.ในเมือง อ.เมือง จ.ชัยภูมิ
                        36000
                      </div>
                    </Radio>
                  </div>
                </div>
              </Radio.Group>
            ) : (
              <div className="mt-4">
                <div
                  className="border border-primary rounded-lg p-4 flex justify-between items-center cursor-pointer"
                  onClick={() => setOpenShippingDrawer(true)}
                >
                  <div>{getShippingMethodLabel(shippingType)}</div>
                  <ChevronRight className="w-5 h-5" />
                </div>
                <Drawer
                  title="เลือกวิธีการจัดส่ง"
                  placement="bottom"
                  onClose={() => setOpenShippingDrawer(false)}
                  open={openShippingDrawer}
                  height={500}
                >
                  <Radio.Group
                    onChange={onChangeShippingType}
                    value={shippingType}
                    className="w-full"
                  >
                    <div className="flex gap-4 flex-col">
                      <div>
                        <Radio value={E_DELIVERY_METHOD.TRANSFER}>
                          จัดส่งสินค้าผ่านขนส่งเอกชน :
                          จัดส่งสินค้าผ่านขนส่งเอกชน
                        </Radio>
                      </div>
                      <div>
                        <Radio
                          value={E_DELIVERY_METHOD.NARIN_TRANSFER}
                          className="radio-label-align-top"
                        >
                          <div>
                            <div className="flex gap-4">
                              <div>รับสินค้าผ่านรถขนส่งของทางร้าน</div>
                              <div
                                className="text-[#828282]"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setShowShippingDetail(!showShippingDetail);
                                }}
                              >
                                {showShippingDetail ? (
                                  <div>
                                    แสดงน้อยลง <ArrowUpIcon />
                                  </div>
                                ) : (
                                  <div>
                                    ดูรายละเอียด <ArrowDownIcon />
                                  </div>
                                )}
                              </div>
                            </div>
                            {showShippingDetail && (
                              <div className="animate-fadeIn">
                                <div>
                                  &#x2022; วันจันทร์ : ในเมืองและในเขตอำเภอเมือง
                                </div>
                                <div>
                                  &#x2022; วันอังคาร:
                                  แก้งคร้อ-คอนสวรรค์-บ้านแท่น
                                </div>
                                <div>
                                  &#x2022; วันพุธ:
                                  บ้านเขว้า-หนองบัวแดง-ภักดีชุมพล
                                </div>
                                <div>
                                  &#x2022; วันพฤหัสฯ:
                                  ภูเขียว-เกษตรสมบูรณ์-คอนสาร
                                </div>
                                <div>
                                  &#x2022; วันศุกร์:
                                  จัตุรัส-เนินสง่า-บำเหน็จณรงค์
                                </div>
                                <div>
                                  &#x2022; วันเสาร์:
                                  หนองบัวระเหว-ซับใหญ่-เทพสถิต
                                </div>
                              </div>
                            )}
                          </div>
                        </Radio>
                      </div>
                      <div>
                        <Radio
                          value={E_DELIVERY_METHOD.AROUND_CITY}
                          className="radio-label-align-top"
                        >
                          <div>รับสินค้าภายในเขตเทศบาลเมืองชัยภูมิ :</div>
                          <div>
                            &#x2022; ส่งฟรี รัศมีจากสาขาเยื้องประปา 1 ไม่เกิน 5
                            กิโลเมตร
                          </div>
                        </Radio>
                      </div>
                      <div>
                        <Radio value={E_DELIVERY_METHOD.RESELL_BRANCH}>
                          <div>
                            รับสินค้าที่บริษัทนรินทร์เภสัช จำกัด
                            สาขาเยื้องประปาชัยภูมิ :
                          </div>
                          <div>
                            &#x2022; เวลาทำการ วันจันทร์ - วันเสาร์ เวลา 08.00
                            น. - 18.00 น. 291 ถ.ชัยภูมิ-สีคิ้ว ต.ในเมือง อ.เมือง
                            จ.ชัยภูมิ 36000
                          </div>
                        </Radio>
                      </div>
                      <div>
                        <Radio value={E_DELIVERY_METHOD.MAIN_BRANCH}>
                          <div>
                            รับสินค้าที่ บริษัทนรินทร์เภสัช จำกัด สาขา
                            สาขาเทศบาล 1
                          </div>
                          <div>
                            &#x2022; เวลาทำการ วันจันทร์ - วันเสาร์ เวลา 08.00
                            น. - 18.00 น. 237/35 ถ.ยุติธรรม ต.ในเมือง อ.เมือง
                            จ.ชัยภูมิ 36000
                          </div>
                        </Radio>
                      </div>
                    </div>
                  </Radio.Group>
                </Drawer>
              </div>
            )}
          </div>
        </div>
        <div className="col-span-1 bg-white p-4">
          <div className="text-xl font-semibold">การชำระเงิน</div>
          <div>รูปแบบการชำระเงิน:</div>
          <Radio.Group
            onChange={onChangePaymentMethod}
            style={{ width: "100%" }}
            defaultValue={E_PAYMENT_METHOD.TRANSFER}
          >
            <div className="flex gap-4 flex-col mt-4">
              <div>
                <Radio value={E_PAYMENT_METHOD.CASH}>ชำระเงินสด </Radio>
              </div>
              <div>
                <Radio value={E_PAYMENT_METHOD.TRANSFER}>
                  โอน / ชำระผ่านบัญชีธนาคาร
                </Radio>
              </div>
            </div>
          </Radio.Group>
          <div className="border-dashed border-1 border-[#6DA011] rounded-lg p-4 mt-4 flex gap-4 justify-evenly">
            <img src="/kbank.png" width={65} height={65} />
            <div className="text-sm">
              <div>
                <span>ชื่อบัญชี </span>
                <span className="font-semibold">บจก.นรินทร์เภสัช</span>
              </div>
              <div>
                <span>สาขา </span>
                <span className="font-semibold">ชัยภูมิ</span>
              </div>
              <div>
                <span>เลขที่บัญชี </span>
                <span className="font-semibold">021 1 78884 4</span>
              </div>
            </div>
          </div>
          <Row gutter={16}>
            <Col span={24} className="p-4">
              <div
                className="w-full bg-[#F8F8F9] p-2 px-4 cursor-pointer justify-between flex"
                onClick={onOpenDrawer}
              >
                <small className="flex gap-4 items-center">
                  <Ticket />
                  {promotionCode ? (
                    <div>
                      ใช้ 1 สิทธิ์{" "}
                      <span className="font-semibold">
                        ลด {discountAmount.toLocaleString()} บาท
                      </span>
                    </div>
                  ) : (
                    "โค้ดส่วนลด"
                  )}
                </small>
                <ChevronRight />
              </div>
            </Col>
            <Col span={24} className="px-4">
              <PointsToggle
                key={`points-toggle-${promotionCode || "no-promo"}`}
                orderAmount={sum - (promotionCode ? discountAmount : 0)}
                cartItemIds={productItemsSelected.map((item) => item.id)}
                promotionCode={promotionCode}
                redemptionItemsPoints={totals.totalPoints}
                onApplied={(discount, pointsUsedAmount) => {
                  setCoinsDiscount(discount);
                  setPointsUsed(pointsUsedAmount || 0);
                }}
              />
            </Col>
            {promotionCode ? (
              <>
                <PriceRow
                  label="ราคารวม"
                  value={sum}
                  valueClassName="text-end py-2 px-8 font-semibold text-sm"
                />
                <PriceRow
                  label="ส่วนลดจากโค้ด"
                  value={discountAmount}
                  isNegative
                />
                {coinsDiscount > 0 && (
                  <>
                    <PriceRow
                      label={`ใช้ ${pointsUsed.toLocaleString()} คะแนน`}
                      value={coinsDiscount}
                      isNegative
                    />
                    {/* <PriceRow
                      label="คะแนนที่ใช้แลกส่วนลด"
                      value={pointsUsed}
                      icon={<StarIcon />}
                      isNegative
                    /> */}
                  </>
                )}
                {/* <Col className="py-2 px-8 text-sm" span={12}>
                  ราคารวมหลังหักส่วนลด
                </Col>
                <Col
                  className="text-end py-2 px-8 font-semibold text-sm"
                  span={12}
                >
                  {(sum - discountAmount - coinsDiscount).toLocaleString()} บาท
                </Col> */}
                {totals.totalPoints > 0 && (
                  <>
                    <PriceRow
                      label="คะแนนที่ใช้แลกสิ่งของ"
                      value={totals.totalPoints}
                      icon={<StarIcon />}
                      isNegative
                    />
                  </>
                )}
                {/* {coinsDiscount > 0 && (
                  <>
                    <PriceRow
                      label="คะแนนที่ใช้สุทธิ"
                      value={pointsUsed + totals.totalPoints}
                      icon={<StarIcon />}
                      isNegative
                    />
                  </>
                )} */}
                <PriceRow
                  label="คะแนนที่ได้รับจากบิลนี้"
                  value={Math.floor(
                    (sum - discountAmount - coinsDiscount) / 25
                  )}
                  icon={<StarIcon />}
                  valueClassName="text-end py-2 px-8 text-sm text-primary"
                />
                <Col
                  span={12}
                  className="py-2 px-8 text-sm bg-[#f7ffeae8]"
                ></Col>
                <Col span={12} className="text-end pt-2 bg-[#f7ffeae8] pr-7">
                  <div className="text-sm">ยอดรวมสุทธิ</div>
                  <div className="text-lg font-semibold">
                    {(sum - discountAmount - coinsDiscount).toLocaleString()}{" "}
                    บาท
                  </div>
                </Col>
                <Col span={24} className="text-end  bg-[#f7ffeae8] pr-7">
                  <div className="text-xs text-success-700">
                    วันนี้คุณประหยัดไปถึง{" "}
                    {(discountAmount + coinsDiscount).toLocaleString()} บาท
                  </div>
                </Col>
              </>
            ) : (
              <>
                <PriceRow
                  label="ราคารวม"
                  value={sum}
                  valueClassName="text-end py-2 px-8 font-semibold text-sm"
                />
                {coinsDiscount > 0 && (
                  <>
                    <PriceRow
                      label={`ใช้ ${pointsUsed.toLocaleString()} คะแนน`}
                      value={coinsDiscount}
                      isNegative
                    />
                  </>
                )}
                {/* Points used from redemption items (if any redemption items selected) */}
                {totals.totalPoints > 0 && (
                  <>
                    <PriceRow
                      label="คะแนนที่ใช้แลกสิ่งของ"
                      value={totals.totalPoints}
                      icon={<StarIcon />}
                      isNegative
                    />
                  </>
                )}
                {/* {coinsDiscount > 0 && (
                  <>
                    <PriceRow
                      label="คะแนนที่ใช้สุทธิ"
                      value={pointsUsed + totals.totalPoints}
                      icon={<StarIcon />}
                      isNegative
                    />
                  </>
                )} */}
                <PriceRow
                  label="คะแนนที่ได้รับจากบิลนี้"
                  value={Math.floor((sum - coinsDiscount) / 25)}
                  icon={<StarIcon />}
                  valueClassName="text-end py-2 px-8 text-sm text-primary"
                />

                <Col span={12} className="py-2 px-8 text-sm bg-[#f7ffeae8]" />
                <Col span={12} className="text-end py-2 bg-[#f7ffeae8] pr-7">
                  <div className="text-sm">ยอดรวมสุทธิ</div>
                  <div className="text-lg font-semibold">
                    {(sum - coinsDiscount).toLocaleString()} บาท
                  </div>
                </Col>
                {coinsDiscount ? (
                  <Col span={24} className="text-end  bg-[#f7ffeae8] pr-7">
                    <div className="text-xs text-success-700">
                      วันนี้คุณประหยัดไปถึง{" "}
                      {(discountAmount + coinsDiscount).toLocaleString()} บาท
                    </div>
                  </Col>
                ) : (
                  <></>
                )}
              </>
            )}
          </Row>
          <Row gutter={16} className="mt-4">
            <Col span={24} md={12}>
              <Button
                onClick={() => setReceiptModalVisible(true)}
                className="mb-2 md:mb-0"
                size="large"
                block
              >
                สรุปรายการสั่งซื้อ
              </Button>
            </Col>
            <Col span={24} md={12}>
              <Button
                onClick={onCheckout}
                size="large"
                type="primary"
                block
                loading={isPending}
              >
                ยืนยันการสั่งซื้อ
              </Button>
            </Col>
          </Row>
        </div>
      </div>
      <Drawer
        onClose={onCloseDrawer}
        open={openCartDrawer}
        title="ใช้ส่วนลดและสิทธิประโยชน์"
        rootClassName="promotion-code-drawer"
      >
        <PromotionCodeDrawer
          onClose={onCloseDrawer}
          sum={sum}
          applyPromotionCode={applyPromotionCode}
          selectedPromotionCode={promotionCode}
          productIds={productIds}
          cartItems={productItemsSelected}
        />
      </Drawer>

      {/* Order Summary Receipt Modal */}
      <ReceiptModal
        isOpen={receiptModalVisible}
        onClose={() => setReceiptModalVisible(false)}
        productItems={productItemsSelected}
        subtotal={sum}
        discount={(promotionCode ? discountAmount : 0) + coinsDiscount}
        promotionCode={promotionCode}
        deliveryCost={0}
        total={sum - discountAmount - coinsDiscount}
        points={
          totals.totalCash > 0
            ? Math.floor(
                (totals.totalCash - discountAmount - coinsDiscount) / 25
              )
            : 0
        }
        pointsUsed={totals.totalPoints}
        shippingAddress={
          userAddress && userAddressId
            ? userAddress.find((addr) => addr.id === userAddressId)?.address
            : undefined
        }
        shippingMethod={getShippingMethodLabel(shippingType)}
        paymentMethod={
          paymentMethod === E_PAYMENT_METHOD.CASH ? "เงินสด" : "โอนเงิน"
        }
      />

      {/* Acceptance Condition Modal */}
      <AcceptanceConditionModal
        isOpen={acceptanceModalVisible}
        onClose={() => {
          setProductItemsSelected([]);
          setAcceptanceModalVisible(false);
          navigate(`/orderdetail/${orderId}`);
        }}
        isFirstTime={isFirstTimeAcceptance}
        onAccept={async () => {
          try {
            await acceptConditionAsync();
            // After accepting conditions, navigate to order detail
            if (orderId) {
              navigate(`/orderdetail/${orderId}`);
              setProductItemsSelected([]);
            }
          } catch (error) {
            console.error("Error accepting conditions:", error);
            // Still navigate to order detail even if accepting conditions fails
            if (orderId) {
              navigate(`/orderdetail/${orderId}`);
            }
          }
        }}
      />
    </NarinLayout>
  );
};
export default CheckoutPage;
